import { useContext, useState } from "react";
import { useAuth } from "react-oidc-context";

import councilPortalService from "api/councilPortalService";
import editorService from "api/editorService";
import pluginService from "api/pluginService";
import useDocumentEditor from "hooks/useDocumentEditor";
import KeysContext from "contexts/KeysContext";
import { ICouncilSession } from "dto/Council/ICouncil";
import { ItemKind } from "dto/IKindItems";
import { generateSingleBinary } from "utils/GenerateSingleBinary";
import { getKeys } from "utils/getKeys";
import { getUserId } from "utils/getUserId";

// Biuro rady → protokół sesji.
//
// Odwzorowanie GenerateCouncilSessionProtocol z Magistratu, gdzie serwer składał
// dokument Word z dziennika przebiegu obrad. W Nocie skład wykonuje wtyczka desktop:
// treść dokumentów edytowalnych jest szyfrowana kluczem obiektu, który powstaje
// w przeglądarce i którego serwer nigdy nie widzi.
//
// Przebieg (trzy strony, każda robi to, co tylko ona umie):
//   1. przeglądarka — generuje klucz obiektu i zakłada PUSTY dokument edytowalny;
//   2. backend — wiąże dokument z sesją, żeby protokół powstał tylko raz;
//   3. wtyczka — pobiera przebieg sesji, składa DOCX i wgrywa go jako treść dokumentu.
// Na końcu dokument otwiera się w Wordzie jak każde inne pismo.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const useCouncilProtocol = () => {
  const auth = useAuth();
  const { keys, setKeys } = useContext(KeysContext);

  const { mutateAsync: createDocument } = editorService.useCreateDocument();
  const { mutateAsync: bindDocument } = councilPortalService.useBindProtocolDocument();
  const { openEditor } = useDocumentEditor();
  const { mutateAsync: checkPlugin } = pluginService.useGetVersionMutated();

  const [busy, setBusy] = useState(false);

  /** Otwiera istniejący protokół w edytorze (Word przez wtyczkę albo Euro-Office — wg ustawień). */
  const open = (documentId: string) =>
    openEditor({ Id: documentId, Kind: ItemKind.EditorDocument });

  /**
   * Składa protokół dla zakończonej sesji i otwiera go w Wordzie.
   * Zwraca identyfikator dokumentu albo rzuca wyjątek z komunikatem dla użytkownika.
   */
  const generate = async (session: ICouncilSession): Promise<string> => {
    setBusy(true);

    try {
      // Bez wtyczki nie ma czym złożyć dokumentu — sprawdzamy to ZANIM założymy
      // dokument w Nocie, żeby nie zostawiać pustego pisma na Dysku.
      const version = await checkPlugin(undefined);

      if (!version) throw new Error("Wtyczka Nota nie odpowiada.");

      const userId = getUserId(auth.user!);
      const myKeys = await getKeys(userId);

      // Protokół nie należy do żadnej teczki, więc klucz obiektu owijamy kluczem
      // pośrednim użytkownika — tak samo jak dokument zakładany bez sprawy.
      const objectKeys = await generateSingleBinary(
        undefined,
        myKeys?.intermidatePrivateKey,
      );

      if (!objectKeys) throw new Error("Nie udało się przygotować klucza dokumentu.");

      const title = `Protokół sesji ${session.Number ?? ""}`.trim();

      const created = await createDocument({
        CaseId: null,
        NewTags: [],
        PrivateKey: objectKeys.PrivateKey,
        PublicKey: objectKeys.PublicKey,
        TagIds: [],
        TemplateId: null,
        Title: title,
        Format: "Docx",
      });

      const documentId = created.data.Id;

      // Klucz musi trafić do kontekstu, zanim wtyczka po niego sięgnie — inaczej
      // pobranie dokumentu skończy się „nie odnaleziono klucza deszyfrującego".
      setKeys([
        ...keys,
        {
          Id: created.data.KeyId,
          PrivateKey: objectKeys.PrivateKey,
          PublicKey: objectKeys.PublicKey,
          ParentKeyId: undefined,
          Armored: false,
          Initial: false,
        },
      ]);

      // Wiązanie przed składem: gdyby skład padł, sesja i tak wskazuje ten dokument,
      // więc kolejna próba nie założy drugiego protokołu na Dysku.
      await bindDocument({ sessionId: session.Id, documentId });

      const result = await pluginService.generateCouncilProtocol({
        SessionId: session.Id,
        DocumentId: documentId,
      });

      if (!result?.Ok)
        throw new Error(result?.Error ?? "Nie udało się złożyć protokołu.");

      open(documentId);

      return documentId;
    } catch (e) {
      throw new Error(errorText(e, (e as Error)?.message ?? "Nie udało się złożyć protokołu."));
    } finally {
      setBusy(false);
    }
  };

  return { generate, open, busy };
};

export default useCouncilProtocol;
